import React, {useRef, useState, useEffect} from "react";
import {Link} from "react-router-dom";

const sections = [
  {key: "acceptance", label: "Acceptance of terms"},
  {key: "accounts", label: "User accounts"},
  {key: "ip", label: "Intellectual Property"},
  {key: "privacy", label: "Privacy and Data"},
  {key: "service", label: "Service Modifications"},
  {key: "liability", label: "Limitation of Liability"},
  {key: "contact", label: "Contact information"},
];

const Terms: React.FC = () => {
  // Refs for scrolling
  const sectionRefs = {
    acceptance: useRef<HTMLDivElement>(null),
    accounts: useRef<HTMLDivElement>(null),
    ip: useRef<HTMLDivElement>(null),
    content: useRef<HTMLDivElement>(null),
    privacy: useRef<HTMLDivElement>(null),
    service: useRef<HTMLDivElement>(null),
    liability: useRef<HTMLDivElement>(null),
    contact: useRef<HTMLDivElement>(null),
  };

  const [active, setActive] = useState("acceptance");

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({key}) => {
        const ref = sectionRefs[key as keyof typeof sectionRefs];
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          return {key, top: rect.top};
        }
        return {key, top: Infinity};
      });
      const visible = offsets.filter(
        (o) => o.top < window.innerHeight * 0.33 && o.top > -200
      );
      if (visible.length > 0) {
        setActive(visible[visible.length - 1].key);
      } else {
        setActive(sections[0].key);
      }
    };
    window.addEventListener("scroll", handleScroll, {passive: true});
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSidebarClick = (key: string) => {
    const ref = sectionRefs[key as keyof typeof sectionRefs];
    if (ref && ref.current) {
      ref.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
    setActive(key);
  };

  return (
    <div className="legal-page sidebar-layout">
      {/* Sidebar */}
      <aside className="sidebar-aside">
        <div className="sidebar-header">CATEGORIES</div>
        <nav>
          {sections.map((section) => (
            <button
              key={section.key}
              onClick={() => handleSidebarClick(section.key)}
              className={`sidebar-link${active === section.key ? " sidebar-link-active" : ""}`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="container sidebar-content">
        <h1 className="legal-title">Terms & Conditions</h1>
        <div className="legal-date">Last updated: March 15, 2025</div>

        {/* 1. Acceptance of Terms */}
        <section
          ref={sectionRefs.acceptance}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">1. Acceptance of Terms</h2>
          <p>
            By accessing and using the League of Fabled Pursuits (LFP) website
            and services, you agree to be bound by these Terms and Conditions.
            If you do not agree with any part of these terms, you may not use
            our services.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 2. User accounts */}
        <section
          ref={sectionRefs.accounts}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">2. User accounts</h2>
          <p>To use our services, you must:</p>
          <ul className="legal-list">
            <li>Be at least 13 years of age</li>
            <li>Register for an account with accurate information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
          </ul>
        </section>
        <hr className="legal-divider" />

        {/* 3. Intellectual Property */}
        <section
          ref={sectionRefs.ip}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">3. Intellectual Property</h2>
          <p>All content on the LFP platform, including but not limited to:</p>
          <ul className="legal-list">
            <li>Website design and interface</li>
            <li>Original campaign content and stories</li>
            <li>Artwork and graphics</li>
            <li>League-specific rules and mechanics</li>
          </ul>
          <p>
            are the property of LFP and protected by intellectual property laws.
          </p>
        </section>
        <hr className="legal-divider" />

        {/* 4. Privacy and Data */}
        <section
          ref={sectionRefs.privacy}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">4. Privacy and Data</h2>
          <p>
            We are committed to protecting your privacy. Our data practices
            include:
          </p>
          <ul className="legal-list">
            <li>Collecting only necessary information</li>
            <li>Securing data with industry-standard measures</li>
            <li>Never selling personal information</li>
            <li>Providing transparency about data usage</li>
          </ul>
        </section>
        <hr className="legal-divider" />

        {/* 5. Service Modifications */}
        <section
          ref={sectionRefs.service}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">6. Service Modifications</h2>
          <p>LFP reserves the right to:</p>
          <ul className="legal-list">
            <li>Modify or discontinue services</li>
            <li>Update these terms with notice</li>
            <li>Change features or functionality</li>
            <li>Adjust user requirements or restrictions</li>
          </ul>
        </section>
        <hr className="legal-divider" />

        {/* 6. Limitation of Liability */}
        <section
          ref={sectionRefs.liability}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">7. Limitation of Liability</h2>
          <p>
            LFP is provided "as is" without warranties. We are not liable for:
          </p>
          <ul className="legal-list">
            <li>Service interruptions or downtime</li>
            <li>Data loss or corruption</li>
            <li>User disputes or conflicts</li>
            <li>Indirect or consequential damages</li>
          </ul>
        </section>
        <hr className="legal-divider" />

        {/* 7. Contact Information */}
        <section
          ref={sectionRefs.contact}
          className="legal-section legal-section-content"
        >
          <h2 className="legal-section-title">8. Contact Information</h2>
          <p>For questions about these terms, contact us at:</p>
          <ul className="legal-list">
            <li>
              <Link to="/feedback" className="legal-link">
                Feedback Form
              </Link>
            </li>
            <li>Discord: LFP Official Server</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default Terms;
